import {
  type ApiClient,
  type BroadcastState,
  broadcastStateOffline,
  type NowPlaying,
  nowPlayingEmpty,
} from "@riq/api";
import {
  type AutoPlayoutEvent,
  AutoPlayout,
  evaluatePreload,
  HtmlAudioPlayer,
  type NowPlayingSchedule,
  registerAudioStreamForAnalysis,
  StationIdleClock,
  StationSyncController,
} from "@riq/audio";
import {
  DEMO_AUDIO_STREAM_URL,
  type LinearPlaybackSource,
  resolveLinearTarget,
  resolveStationAudioUrl,
  STATUS_POLL_INTERVAL_SECONDS,
} from "@riq/shared";
import {
  cleanupEgressPipeline,
  createEgressApi,
  createMetadataChannelApi,
  type EgressPipelineResult,
  getOrCreateLiveAudioElement,
  playLiveAudioElement,
  primeLiveAudioElement,
  runEgressPipeline,
  type StepTimings,
  simulateEgressSteps,
  subscribeMetadataChannel,
  type WebRtcConnectionStep,
} from "@riq/webrtc";
import { BroadcastLiveness, type BroadcastLivenessSignal } from "./broadcast-liveness.js";
import type {
  LinearPlaybackCopy,
  LinearPlaybackEngineOptions,
  LinearPlaybackSnapshot,
} from "./types.js";

function toSchedule(np: NowPlaying): NowPlayingSchedule {
  return {
    available: np.available,
    serverTime: np.serverTime,
    anchorMs: np.anchorMs,
    totalDuration: np.totalDuration,
    currentIndex: np.currentIndex,
    offsetSeconds: np.offsetSeconds,
    playlist: np.playlist,
  };
}

/**
 * Deep module for live Broadcast egress and Auto station playback.
 * One interface for tune-in, handoff, and Broadcast liveness — shared by Tuner and embed.
 */
export class LinearPlaybackEngine {
  private readonly api: ApiClient;
  private readonly copy: LinearPlaybackCopy;
  private readonly visualizer: boolean;
  private readonly stickyEngagedLinearTarget: boolean;
  private readonly onRemoteStream?: (stream: MediaStream) => void;
  private readonly formatError: (error: unknown) => string;
  private readonly logPrefix: string;

  private readonly listeners = new Set<(snapshot: LinearPlaybackSnapshot) => void>();
  private lastEmitKey = "";

  private broadcastState: BroadcastState = broadcastStateOffline();
  private schedule: NowPlaying = nowPlayingEmpty();

  private activeSource: LinearPlaybackSource | null = null;
  private livePlaying = false;
  private liveTuningIn = false;
  private stationPlaying = false;
  private stationTuningIn = false;
  private connectionStep: WebRtcConnectionStep | null = null;
  private error: string | null = null;
  private volume = 0.8;
  private stationCurrentIndex = 0;
  private stationPositionMs = 0;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private egress: EgressPipelineResult | null = null;
  private liveAudioElement: HTMLAudioElement | null = null;
  private started = false;
  private statusPoll: Promise<void> | null = null;
  private refreshInFlight: Promise<void> | null = null;
  private playGeneration = 0;

  private presenceSocket: WebSocket | null = null;

  /**
   * Per-step breakdown of the last live tune-in. Whether pre-warming the
   * listener session is worth building depends on real client RTT, which only
   * production traffic can answer — so keep the measurement to hand.
   */
  private lastLiveConnectTimings: StepTimings | null = null;
  private metadataChannel: RTCDataChannel | null = null;

  private preloadEl: HTMLAudioElement | null = null;
  private preloadedUrl: string | null = null;

  private readonly stationPlayer: HtmlAudioPlayer;
  private readonly stationSync = new StationSyncController();
  private readonly stationPlayout = new AutoPlayout(this.stationSync);
  private stationWakeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly liveness = new BroadcastLiveness();
  /**
   * Bumped when the Broadcast ends, so a status response that started
   * earlier cannot mark it live again.
   */
  private livenessEpoch = 0;
  private disconnecting: Promise<void> | null = null;
  private remoteAudio: MediaStream | null = null;
  private remoteAudioStop: (() => void) | null = null;
  private trackCheckInFlight = false;
  private readonly demoPlayer: HtmlAudioPlayer;

  private readonly idleClock: StationIdleClock;

  constructor(api: ApiClient, options: LinearPlaybackEngineOptions) {
    this.api = api;
    this.copy = options.copy;
    this.visualizer = options.visualizer ?? false;
    this.stickyEngagedLinearTarget = options.stickyEngagedLinearTarget ?? false;
    this.onRemoteStream = options.onRemoteStream;
    this.formatError = options.formatError ?? (() => this.copy.generic);
    this.logPrefix = options.logPrefix ?? "[Riq Linear]";

    const playerOpts = this.visualizer
      ? { crossOrigin: "anonymous" as const, visualizer: true }
      : undefined;

    this.stationPlayer = new HtmlAudioPlayer(playerOpts);
    this.demoPlayer = new HtmlAudioPlayer(playerOpts);

    this.idleClock = new StationIdleClock(this.stationSync, (index) => {
      this.stationCurrentIndex = index;
      this.emit();
    });

    this.stationPlayer.on("timeupdate", () => this.onStationTimeUpdate());
    this.stationPlayer.on("ended", () => {
      void this.applyAutoVerdict("ended");
    });
  }

  get snapshot(): LinearPlaybackSnapshot {
    return {
      broadcastState: this.broadcastState,
      schedule: this.schedule,
      activeSource: this.activeSource,
      livePlaying: this.livePlaying,
      liveTuningIn: this.liveTuningIn,
      stationPlaying: this.stationPlaying,
      stationTuningIn: this.stationTuningIn,
      connectionStep: this.connectionStep,
      error: this.error,
      volume: this.volume,
      stationCurrentIndex: this.stationCurrentIndex,
      stationPositionMs: this.stationPositionMs,
    };
  }

  /** Step timings for the most recent live tune-in, or null if none succeeded. */
  get liveConnectTimings(): StepTimings | null {
    return this.lastLiveConnectTimings;
  }

  onChange(callback: (snapshot: LinearPlaybackSnapshot) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    void this.checkServerStatus();
    void this.refreshSchedule();
    this.pollTimer = setInterval(
      () => void this.checkServerStatus(),
      STATUS_POLL_INTERVAL_SECONDS * 1000,
    );
    this.log("Initialized.");
    this.emit();
  }

  dispose(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
    this.clearStationWake();
    this.started = false;
    this.idleClock.stop();
    void this.stopAll();
    this.stationPlayer.dispose();
    this.demoPlayer.dispose();
  }

  resolveLinearTarget(): LinearPlaybackSource | null {
    if (this.stickyEngagedLinearTarget && this.activeSource === "station" && this.isEngaged()) {
      return "station";
    }
    return resolveLinearTarget(this.broadcastState, this.schedule.available);
  }

  isPlaying(source: LinearPlaybackSource = this.activeSource ?? "live"): boolean {
    if (source === "live") return this.livePlaying;
    return this.stationPlaying;
  }

  isTuningIn(source: LinearPlaybackSource = this.activeSource ?? "live"): boolean {
    if (source === "live") return this.liveTuningIn;
    return this.stationTuningIn;
  }

  isEngaged(source?: LinearPlaybackSource): boolean {
    if (source) return this.isPlaying(source) || this.isTuningIn(source);
    return this.livePlaying || this.liveTuningIn || this.stationPlaying || this.stationTuningIn;
  }

  currentStationTrack() {
    if (!this.schedule.available) return null;
    const idx = this.stationCurrentIndex;
    if (idx < 0 || idx >= this.schedule.playlist.length) return null;
    return this.schedule.playlist[idx] ?? null;
  }

  primeLiveAudio(): void {
    const active = this.broadcastState;
    if (!active.isLive || active.isDemo) return;
    primeLiveAudioElement(this.ensureLiveAudioElement());
  }

  async toggleLinear(): Promise<void> {
    if (this.isEngaged()) {
      await this.stopActive();
      return;
    }
    if (this.resolveLinearTarget() === "live") {
      this.primeLiveAudio();
    }
    await this.tuneLinear();
  }

  async tuneLinear(options?: { refreshStatus?: boolean }): Promise<void> {
    if (options?.refreshStatus !== false) {
      await this.checkServerStatus();
    }
    const target = this.resolveLinearTarget();
    if (!target) return;

    if (this.activeSource !== target) {
      if (target === "live") await this.stopStation();
      else await this.disconnectLive();
      this.activeSource = target;
    }

    if (target === "live") await this.connectLive();
    else await this.tuneStation();
  }

  async connectLive(): Promise<void> {
    const active = this.broadcastState;
    if (!active.isLive) {
      this.error = this.copy.noLiveBroadcast;
      this.log("Cannot connect: no active live Broadcast.");
      this.emit();
      return;
    }

    this.liveTuningIn = true;
    this.liveness.decide({ type: "session-started" });
    this.error = null;
    this.connectionStep = "peer";
    this.activeSource = "live";
    this.emit();

    const setConnectionStep = (step: WebRtcConnectionStep): void => {
      this.connectionStep = step;
      this.emit();
    };

    try {
      if (active.isDemo) {
        this.log("[DEMO] Mock mode — simulated stream.");
        await simulateEgressSteps(setConnectionStep);
        await this.startDemoPlayback();
        setConnectionStep("connected");
        this.livePlaying = true;
        this.liveTuningIn = false;
        this.connectionStep = null;
        this.emit();
        return;
      }

      if (!active.sessionId || !active.trackId) {
        throw new Error(this.copy.missingWebRtc);
      }

      const audioElement = this.ensureLiveAudioElement();
      primeLiveAudioElement(audioElement);

      this.egress = await runEgressPipeline(
        createEgressApi(this.api),
        {
          isLive: active.isLive,
          isDemo: active.isDemo,
          sessionId: active.sessionId,
          trackId: active.trackId,
        },
        {
          audioElement,
          onLog: (m) => this.log(m),
          onStep: setConnectionStep,
          onConnectionState: (state) => {
            this.log(`WebRTC connection state: ${state}`);
            if (state === "connected") {
              setConnectionStep("connected");
            } else if (state === "failed" && this.liveSessionUp()) {
              this.applyLiveness({ type: "peer-failed" });
            }
          },
          onRemoteStream: (stream) => {
            this.watchRemoteAudio(stream);
            if (this.visualizer) registerAudioStreamForAnalysis(stream);
            this.onRemoteStream?.(stream);
            if (this.egress) void this.playLiveAudio(this.egress.audioElement);
          },
        },
      );

      this.lastLiveConnectTimings = this.egress.timings;
      this.connectPresenceSocket();
      await this.playLiveAudio(this.egress.audioElement);
      // Metadata costs two round trips and a renegotiation, so it is wired up
      // only once audio is already playing.
      void this.subscribeToMetadata();
      setConnectionStep("connected");
      this.livePlaying = true;
      this.liveTuningIn = false;
      this.connectionStep = null;
      this.emit();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.log(`Connection failed: ${msg}`);
      this.error = this.connectionError(e);
      this.liveTuningIn = false;
      this.connectionStep = null;
      await this.disconnectLive();
    }
  }

  async disconnectLive(): Promise<void> {
    if (this.disconnecting) return this.disconnecting;
    this.disconnecting = this.tearDownLive().finally(() => {
      this.disconnecting = null;
    });
    return this.disconnecting;
  }

  private async tearDownLive(): Promise<void> {
    this.log("Disconnecting from Broadcast...");
    this.livePlaying = false;
    this.liveTuningIn = false;
    this.connectionStep = null;
    if (this.activeSource === "live") this.activeSource = null;
    this.liveness.decide({ type: "session-dropped" });
    this.unwatchRemoteAudio();
    this.disconnectPresenceSocket();
    await this.stopDemoPlayback();
    this.cleanupWebRtc();
    this.emit();
  }

  async tuneStation(): Promise<void> {
    this.idleClock.stop();
    this.stationTuningIn = true;
    this.error = null;
    this.emit();

    try {
      const hasCached = this.schedule.available && this.schedule.playlist.length > 0;
      if (!hasCached) {
        await this.refreshSchedule();
      } else {
        void this.refreshSchedule();
      }
      if (!this.schedule.available) {
        this.stationTuningIn = false;
        this.error = this.copy.stationEmpty;
        this.emit();
        return;
      }
      this.activeSource = "station";
      const verdict = this.stationPlayout.decide({
        event: "tune",
        playingIndex: this.stationCurrentIndex,
        positionSeconds: 0,
        schedule: toSchedule(this.schedule),
      });
      if (verdict.type !== "seek") {
        this.stationTuningIn = false;
        this.error = this.copy.stationEmpty;
        this.emit();
        return;
      }
      await this.playStationTrackAt(verdict.index, verdict.offsetSeconds);
      this.log(
        `Tuned in at track ${verdict.index}, offset ${verdict.offsetSeconds.toFixed(1)}s`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.log(`Tune-in failed: ${msg}`);
      this.error = this.formatError(e);
      this.stationTuningIn = false;
      this.stationPlaying = false;
      this.emit();
    }
  }

  async stopStation(): Promise<void> {
    if (!this.stationPlaying && !this.stationTuningIn) return;
    this.stationPlaying = false;
    this.stationTuningIn = false;
    this.clearStationWake();
    this.stationPlayout.reset();
    this.stationPlayer.stop();
    if (this.preloadEl) {
      this.preloadEl.removeAttribute("src");
      this.preloadEl.load();
    }
    this.preloadedUrl = null;
    this.stationPositionMs = 0;
    if (this.activeSource === "station") this.activeSource = null;
    if (this.schedule.available) this.idleClock.start(toSchedule(this.schedule));
    this.log("Station stopped.");
    this.emit();
  }

  async stopActive(): Promise<void> {
    if (this.activeSource === "live") {
      await this.disconnectLive();
    } else if (this.activeSource === "station") {
      await this.stopStation();
    } else {
      await this.stopAll();
    }
  }

  async refreshSchedule(): Promise<void> {
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      try {
        const next = await this.api.fetchNowPlaying();
        this.stationSync.updateClockSkew(next.serverTime);
        this.schedule = next;
        if (!this.isEngaged()) {
          this.idleClock.start(toSchedule(next));
        }
      } catch (e) {
        this.log(`Schedule refresh failed: ${e}`);
      } finally {
        this.refreshInFlight = null;
        this.emit();
      }
    })();

    return this.refreshInFlight;
  }

  async resyncStationFromClock(): Promise<void> {
    if (!this.stationPlaying) return;
    await this.applyAutoVerdict("resume");
  }

  async resumeLivePlayback(): Promise<void> {
    if (!this.livePlaying || this.broadcastState.isDemo || !this.egress) return;
    try {
      await playLiveAudioElement(this.egress.audioElement, { volume: this.volume });
    } catch {
      /* autoplay policy may block without gesture */
    }
  }

  setVolume(value: number): void {
    this.volume = value;
    if (this.broadcastState.isDemo) {
      this.demoPlayer.volume = value;
    } else if (this.egress) {
      this.egress.audioElement.volume = value;
    }
    this.stationPlayer.volume = value;
    this.emit();
  }

  private ensureLiveAudioElement(): HTMLAudioElement {
    this.liveAudioElement = getOrCreateLiveAudioElement(this.liveAudioElement);
    return this.liveAudioElement;
  }

  private emit(): void {
    const snap = this.snapshot;
    const key = JSON.stringify(snap);
    if (key === this.lastEmitKey) return;
    this.lastEmitKey = key;
    for (const cb of this.listeners) cb(snap);
  }

  private log(message: string): void {
    const ts = new Date().toLocaleTimeString();
    console.log(`${this.logPrefix}[${ts}] ${message}`);
  }

  async refreshBroadcastStatus(): Promise<void> {
    await this.checkServerStatus();
  }

  private async checkServerStatus(): Promise<void> {
    if (this.statusPoll) return this.statusPoll;
    this.statusPoll = this.runServerStatusPoll().finally(() => {
      this.statusPoll = null;
    });
    return this.statusPoll;
  }

  private async runServerStatusPoll(): Promise<void> {
    const epoch = this.livenessEpoch;
    try {
      const snapshot = await this.api.fetchBroadcastState();
      if (epoch !== this.livenessEpoch) return;
      this.applyLiveness({
        type: "poll",
        snapshot,
        sessionUp: this.liveSessionUp(),
      });
    } catch {
      if (epoch !== this.livenessEpoch) return;
      this.applyLiveness({ type: "poll-failed" });
    }
  }

  private onStationTimeUpdate(): void {
    if (!this.stationPlaying) return;
    this.stationPositionMs = this.stationPlayer.currentTime * 1000;

    const decision = evaluatePreload(
      this.stationCurrentIndex,
      this.stationPlayer.currentTime,
      this.stationPlayer.duration,
      this.schedule.playlist.length,
    );
    if (decision.shouldPreload) this.warmNextTrack(decision.nextIndex);

    void this.applyAutoVerdict("timeupdate");
  }

  private warmNextTrack(nextIndex: number): void {
    if (typeof Audio === "undefined") return;
    const track = this.schedule.playlist[nextIndex];
    if (!track) return;
    const url = resolveStationAudioUrl(this.api.resolvedBaseUrl, track.audioUrl);
    if (url === this.preloadedUrl) return;
    if (!this.preloadEl) {
      this.preloadEl = new Audio();
      this.preloadEl.preload = "auto";
      this.preloadEl.crossOrigin = "anonymous";
    }
    this.preloadEl.src = url;
    this.preloadEl.load();
    this.preloadedUrl = url;
  }

  private async playStationTrackAt(index: number, offsetSeconds: number): Promise<void> {
    const sch = this.schedule;
    if (index < 0 || index >= sch.playlist.length) return;

    const generation = ++this.playGeneration;
    const track = sch.playlist[index]!;
    const url = resolveStationAudioUrl(this.api.resolvedBaseUrl, track.audioUrl);

    if (
      index === this.stationCurrentIndex &&
      this.stationPlayer.element.src &&
      this.stationPlayer.element.readyState >= HTMLMediaElement.HAVE_METADATA
    ) {
      await this.seekStationTo(index, offsetSeconds);
      if (generation !== this.playGeneration) return;
      this.stationPlaying = true;
      this.stationTuningIn = false;
      this.emit();
      return;
    }

    this.stationPlayer.stop();
    this.stationPlayer.volume = this.volume;
    await this.stationPlayer.setSource(url);
    await this.stationPlayer.waitForLoadedMetadata();
    if (generation !== this.playGeneration) return;
    await this.seekStationTo(index, offsetSeconds);
    if (generation !== this.playGeneration) return;
    this.stationPlaying = true;
    this.stationTuningIn = false;
    this.emit();
  }

  private async seekStationTo(index: number, offsetSeconds: number): Promise<void> {
    const target = Math.max(0, offsetSeconds);
    if (Math.abs(this.stationPlayer.currentTime - target) > 0.05) {
      this.stationPlayer.seek(target);
    }
    this.stationCurrentIndex = index;
    this.stationPositionMs = target * 1000;
    if (this.stationPlayer.paused) {
      await this.stationPlayer.play();
    }
  }

  private async applyAutoVerdict(event: AutoPlayoutEvent): Promise<void> {
    if (!this.stationPlaying) return;
    const verdict = this.stationPlayout.decide({
      event,
      playingIndex: this.stationCurrentIndex,
      positionSeconds: this.stationPlayer.currentTime,
      schedule: toSchedule(this.schedule),
    });
    if (verdict.type === "waitUntil") {
      this.armStationWake(verdict.atMs);
      return;
    }
    if (verdict.type === "hold") return;
    this.clearStationWake();
    await this.playStationTrackAt(verdict.index, verdict.offsetSeconds);
  }

  private armStationWake(atMs: number): void {
    this.clearStationWake();
    const delay = Math.max(0, atMs - Date.now());
    this.stationWakeTimer = setTimeout(() => {
      this.stationWakeTimer = null;
      void this.applyAutoVerdict("timeupdate");
    }, delay);
  }

  private clearStationWake(): void {
    if (this.stationWakeTimer == null) return;
    clearTimeout(this.stationWakeTimer);
    this.stationWakeTimer = null;
  }

  private async stopAll(): Promise<void> {
    if (this.activeSource === "live" || this.livePlaying || this.liveTuningIn) {
      await this.disconnectLive();
      return;
    }
    await this.stopStation();
  }

  private connectPresenceSocket(): void {
    this.disconnectPresenceSocket();
    try {
      this.presenceSocket = new WebSocket(
        `${this.api.wsBaseUrl}/api/broadcast/presence?role=tuner`,
      );
    } catch {
      // ignore fallback
    }
  }

  private disconnectPresenceSocket(): void {
    if (this.presenceSocket) {
      this.presenceSocket.close();
      this.presenceSocket = null;
    }
  }

  async checkAndRecoverPresence(): Promise<void> {
    if (this.activeSource === "live" || this.livePlaying) {
      // Refresh state via HTTP
      await this.refreshBroadcastStatus();

      // Reconnect websocket if it died
      if (
        !this.presenceSocket ||
        this.presenceSocket.readyState === WebSocket.CLOSED ||
        this.presenceSocket.readyState === WebSocket.CLOSING
      ) {
        this.log("Presence socket was dead; reconnecting...");
        this.connectPresenceSocket();
      }
    }
  }

  private async playLiveAudio(el: HTMLAudioElement): Promise<void> {
    await playLiveAudioElement(el, { volume: this.volume });
  }

  /**
   * Subscribe to the Operator's metadata channel through the SFU.
   *
   * Best effort: if it fails, title, speaker and listener count simply keep
   * arriving on the status poll instead.
   */
  private async subscribeToMetadata(): Promise<void> {
    const egress = this.egress;
    if (!egress) return;

    try {
      this.metadataChannel = await subscribeMetadataChannel(
        egress.peerConnection,
        createMetadataChannelApi(this.api),
        egress.listenerSessionId,
      );
      this.metadataChannel.onmessage = (event) => this.applyMetadataMessage(event.data);
      this.log("Subscribed to the broadcast metadata channel.");

      // Studio pushes metadata when the Durable Object reports a listener
      // count change — which happens the moment this listener's presence
      // socket opened, several hundred milliseconds before the subscription
      // above completed. That first push is therefore always missed, and the
      // next one only arrives when someone else joins or leaves. Pull the
      // current state once so a joining listener is correct immediately
      // rather than up to a poll interval later.
      await this.refreshBroadcastStatus();
    } catch (e) {
      this.log(`Metadata channel unavailable (falling back to polling): ${e}`);
      this.metadataChannel = null;
    }
  }

  private applyMetadataMessage(raw: string): void {
    try {
      const metadata = JSON.parse(raw);
      if (metadata.type === "BROADCAST_ENDED") {
        this.applyLiveness({ type: "broadcast-ended", current: this.broadcastState });
        return;
      }
      this.broadcastState = {
        ...this.broadcastState,
        title: metadata.title,
        speaker: metadata.speaker,
        activeListeners: metadata.listeners,
      };
      this.emit();
    } catch {
      // Ignore anything that is not our JSON payload.
    }
  }

  private cleanupWebRtc(): void {
    this.unwatchRemoteAudio();
    if (this.metadataChannel) {
      this.metadataChannel.onmessage = null;
      this.metadataChannel.close();
      this.metadataChannel = null;
    }
    if (!this.egress) return;
    cleanupEgressPipeline(this.egress);
    this.egress = null;
  }

  private async startDemoPlayback(): Promise<void> {
    this.demoPlayer.element.loop = true;
    this.demoPlayer.volume = this.volume;
    await this.demoPlayer.setSource(DEMO_AUDIO_STREAM_URL);
    await this.demoPlayer.play();
  }

  private async stopDemoPlayback(): Promise<void> {
    this.demoPlayer.stop();
    this.demoPlayer.element.loop = false;
  }

  private liveSessionUp(): boolean {
    return this.livePlaying || this.liveTuningIn;
  }

  private applyLiveness(signal: BroadcastLivenessSignal): void {
    const verdict = this.liveness.decide(signal);
    if (verdict.type === "keep") return;

    if (verdict.type === "drop-session") {
      if (!this.liveSessionUp()) return;
      this.log("Egress Session lost. Leaving the Broadcast up.");
      void this.disconnectLive();
      this.emit();
      return;
    }

    this.broadcastState = verdict.snapshot;
    if (verdict.type === "show") {
      this.emit();
      return;
    }

    this.livenessEpoch += 1;
    const sessionUp = this.liveSessionUp();
    this.log(sessionUp ? "Broadcast ended. Disconnecting live..." : "Broadcast ended.");
    if (sessionUp) void this.disconnectLive();
    this.emit();
  }

  private watchRemoteAudio(stream: MediaStream): void {
    this.unwatchRemoteAudio();
    const onDead = (): void => {
      this.noteRemoteAudioDead();
    };
    const bind = (track: MediaStreamTrack): void => {
      if (track.kind !== "audio") return;
      track.addEventListener("mute", onDead);
      track.addEventListener("ended", onDead);
    };
    for (const track of stream.getAudioTracks()) bind(track);
    const onAdd = (event: Event): void => {
      bind((event as MediaStreamTrackEvent).track);
    };
    stream.addEventListener("addtrack", onAdd);
    this.remoteAudio = stream;
    this.remoteAudioStop = () => {
      stream.removeEventListener("addtrack", onAdd);
      for (const track of stream.getAudioTracks()) {
        track.removeEventListener("mute", onDead);
        track.removeEventListener("ended", onDead);
      }
    };
  }

  private unwatchRemoteAudio(): void {
    this.remoteAudioStop?.();
    this.remoteAudioStop = null;
    this.remoteAudio = null;
  }

  private noteRemoteAudioDead(): void {
    if (this.trackCheckInFlight || !this.liveSessionUp()) return;
    this.trackCheckInFlight = true;
    const epoch = this.livenessEpoch;
    void this.runTrackCheck(epoch).finally(() => {
      this.trackCheckInFlight = false;
    });
  }

  private remoteAudioRecovered(): boolean {
    const stream = this.remoteAudio;
    if (!stream) return false;
    return stream.getAudioTracks().some((track) => track.readyState === "live" && !track.muted);
  }

  private async runTrackCheck(epoch: number): Promise<void> {
    try {
      const snapshot = await this.api.fetchBroadcastState();
      if (epoch !== this.livenessEpoch) return;
      this.applyLiveness({
        type: "track-check",
        snapshot,
        trackLive: this.remoteAudioRecovered(),
      });
    } catch {
      if (epoch !== this.livenessEpoch) return;
      this.applyLiveness({
        type: "track-check-failed",
        trackLive: this.remoteAudioRecovered(),
      });
    }
  }

  private connectionError(e: unknown): string {
    if (e instanceof Error) {
      const { message } = e;
      if (
        message === this.copy.missingWebRtc ||
        message === this.copy.noLiveBroadcast ||
        message.includes("WebRTC") ||
        message.includes("Cloudflare") ||
        message.includes("Live audio play failed")
      ) {
        return message;
      }
    }
    return this.formatError(e);
  }
}
